import { hapticFeedback } from 'src/services/native_container_requests_ios';
import {
	get_device_component_index,
	get_gen_1_path_from_category,
	real_id,
	get_gen_2_control_component_from_category,
	get_local_ip,
	is_gen_2,
	isDiffuser,
	get_gen_2_control_component_channel_structure,
	isFanDevice,
} from './interface_helpers';
import { localReq } from 'src/services/http';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { gen1CloudReq } from 'src/services/gen_1_reqs';
import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { os, app } from 'src/stores/session/native-bridge';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import { QUICK_TIME_CONTROL_CORRECTION_GEN_1_DEVICES } from 'src/constants';
import {
	readVentoraFanSpeedPercent,
	ventoraClamp,
} from 'src/components/DevicesSettings/Settings/SettingsG2/VentoraFan/ventora_fan_settings_helpers';

const updateControlledComponent = async (device, url, ison, isGen2, status) => {
	const componentStatus = await localReq(
		device,
		url,
		'post',
		{
			id: Date.now(),
			method: `${get_gen_2_control_component_from_category(device)}.GetStatus`,
			params: { id: device.channel, on: !ison },
			src: 'ShellyApp',
		},
		9000,
		!isGen2,
	);

	status[get_gen_2_control_component_channel_structure(device)] = componentStatus;

	devices_status.update_device_status({
		id: real_id(device.id),
		msg: status,
	});
};

/**
 * Helper to handle sending gen1 request.
 * For some gen1 devices, we need to convert `flipBackSeconds` to milliseconds because of device's firmware.
 */
const sendGen1Request = async (device: Device, ison: boolean, flipBackSeconds: number = undefined) => {
	let res = undefined;
	let _flipBackSeconds = flipBackSeconds;

	// For some gen1 devices, we need to convert `flipBackSeconds` to milliseconds; consult issue shelly-app-3#2984
	if (_flipBackSeconds && QUICK_TIME_CONTROL_CORRECTION_GEN_1_DEVICES.includes(device?.type)) {
		_flipBackSeconds = flipBackSeconds * 1000; // from seconds to milliseconds
	}

	res = await gen1CloudReq(device, device.category, {
		turn: ison ? 'off' : 'on',
		...(_flipBackSeconds && { timeout: _flipBackSeconds }),
	});

	return res;
};

/**
 * Helper to handle sending gen2 request
 */
const sendGen2Request = async (
	device: Device,
	component: string,
	index: string,
	ison: boolean,
	flipBackSeconds: number = undefined,
) => {
	// Send request for breaker devices
	if (device?.category === 'breaker') {
		return await sendGen2RequestBreaker(device, component, index, ison);
	}

	// Send request for diffuser devices
	if (isDiffuser(device?.type)) {
		return await sendGen2RequestDiffuser(device, component, index, ison);
	}

	// Ventora fan: `Fan.Set` uses `on` + `speed` 1–100 (fan:0).
	if (isFanDevice(device?.type)) {
		const fanStatus = devices_status.getByRealId(device.id)?.['fan:0'];
		return await gen2Request(device, 'Fan.Set', {
			id: 0,
			mode: fanStatus?.mode || 'manual',
			on: !ison,
			speed: ventoraClamp(
				readVentoraFanSpeedPercent(fanStatus as Record<string, unknown> | undefined),
				1,
				100,
			),
			...(flipBackSeconds && { toggle_after: flipBackSeconds }),
		});
	}

	// Send request for all other device types
	const method = `${component.split(':')[0]}.set`;
	return await gen2Request(device, method, {
		id: index,
		on: !ison,
		...(flipBackSeconds && { toggle_after: flipBackSeconds }),
	});
};

/**
 * Helper to handle sending gen2 request specific for breaker devices
 */
const sendGen2RequestBreaker = async (
	device: Device,
	component: string,
	index: string,
	ison: boolean,
) => {
	let res = undefined;
	const method = `${component.split(':')[0]}.set`;

	res = await gen2Request(device, method, {
		id: index,
		output: !ison,
	});

	return res;
};

/**
 * Helper to handle sending gen2 request specific for diffuser devices
 */
const sendGen2RequestDiffuser = async (
	device: Device,
	component: string,
	index: string,
	ison: boolean,
) => {
	let res = undefined;
	const method = `${component.split(':')[0]}.SetConfig`;

	res = await gen2Request(device, method, {
		id: index,
		config: { ambient: { enable: !ison } },
	});

	return res;
};

export const handleDeviceTurn = async ({ device }, flipBackSeconds: number = undefined) => {
	try {
		let index = device.category === 'emeter' ? 0 : get_device_component_index(device);
		let localIp = get_local_ip(device.id);
		const isGen2 = is_gen_2(device);
		let ison = false;
		let expected_ison = !ison;
		let component = isGen2 ? get_gen_2_control_component_channel_structure(device) : undefined;

		let status = devices_status.getByRealId(device.id);
		let settings = devices_settings.getByRealId(device.id); //needed settings for the diffuser's ambient states
		let res = undefined;

		if (status?.online) {
			if (typeof status?.[device.category.split('s_')[0] + 's'] !== 'undefined') {
				if (device.category === 'emeter') {
					ison = status['relays'][0].ison;
				} else {
					ison = status[device.category.split('s_')[0] + 's'][device.channel].ison;
				}
			} else if (isGen2) {
				if (isDiffuser(device.type)) {
					//getting the diffuser's ambient state
					ison = settings[component]?.ambient?.enable;
					expected_ison = !ison;
				} else {
					ison =
						typeof status?.[component]?.output !== 'undefined'
							? status?.[component]?.output
							: status?.[component]?.on;
				}
			} else {
				ison = false;
			}
		}

		if (app) {
			if (get(os) === 'IOS') {
				hapticFeedback('buttonPressed');
			}
		}

		if (localIp) {
			const url = isGen2
				? `${localIp}/rpc/`
				: `${localIp}/${
						device.category !== 'emeter' && device.category !== 'module'
							? device.category
							: 'relay'
					}/${device.category !== 'emeter' ? device.channel : 0}`;

			const method = isDiffuser(device.type)
				? `${get_gen_2_control_component_from_category(device)}.SetConfig`
				: isFanDevice(device.type)
					? 'Fan.Set'
					: `${get_gen_2_control_component_from_category(device)}.Set`;
			let params: Record<string, unknown> = { id: index, on: !ison };
			if (isDiffuser(device.type)) {
				params = { id: index, config: { ambient: { enable: !ison } } };
			}
			if (isFanDevice(device.type)) {
				params = {
					id: 0,
					mode: status?.['fan:0']?.mode || 'manual',
					on: !ison,
					speed: ventoraClamp(
						readVentoraFanSpeedPercent(
							status?.['fan:0'] as Record<string, unknown> | undefined,
						),
						1,
						100,
					),
				};
			}

			// Include quick timed control if available
			if (flipBackSeconds) {
				if (isGen2) {
					params = { ...params, ...{ toggle_after: flipBackSeconds } };
				}
			}

			const data = isGen2
				? { id: Date.now(), method, params, src: 'ShellyApp' }
				: {
						turn: ison ? 'off' : 'on',
						...(flipBackSeconds && { timeout: flipBackSeconds }),
					};

			if (device.category === 'breaker') {
				data.params.output = !ison;
			}

			res = await localReq(device, url, 'post', data, 9000, !isGen2);

			// failed local control, try cloud request
			if (!res.isok && typeof get(cloud_disabled_devices)[device.id] === 'undefined') {
				console.warn('Local control failed. Will try cloud request.');
				if (isGen2) {
					res = await sendGen2Request(device, component, index, ison, flipBackSeconds);
				} else {
					res = await sendGen1Request(device, ison, flipBackSeconds);
				}
			} else if (get(cloud_disabled_devices)[device.id]) {
				// cloud disabled status pull
				// update component status after local request. Mostly for timers status
				// no need to await this one... only slows down the loading feedback.
				if (isGen2) {
					updateControlledComponent(device, url, ison, isGen2, status);
				} else {
					// cloud disabled status is returned with the request response for gen 1
					status[get_gen_1_path_from_category(device)][
						device.category !== 'emeter' ? device.channel : 0
					] = res;

					devices_status.update_device_status({
						id: real_id(device.id),
						msg: status,
					});
				}
			}
		} else {
			if (!isGen2) {
				res = await sendGen1Request(device, ison, flipBackSeconds);
			} else {
				res = await sendGen2Request(device, component, index, ison, flipBackSeconds);
			}
		}

		return { success: res.isok, expected_ison };
	} catch (error) {
		return { success: false, error };
	}
};
